import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { collection, getDocs, query, where, orderBy, limit, startAfter, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase/config'
import { Calendar, User, ArrowLeft, Loader, FileText, Tag, ArrowRight } from 'lucide-react'

const BLOG_PAGE_SIZE = 9

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const postId = searchParams.get('post')
  const [blogPosts, setBlogPosts] = useState([])
  const [selectedPost, setSelectedPost] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastDoc, setLastDoc] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [postLoading, setPostLoading] = useState(false)
  const [loadingNextForNav, setLoadingNextForNav] = useState(false)
  const loadMoreRef = useRef(null)
  const scrollRestoreRef = useRef(null)

  const fetchPostsPage = useCallback(async (cursor = null, append = false) => {
    try {
      if (append) setLoadingMore(true)
      else setLoading(true)
      const base = collection(db, 'blogPosts')
      const q = cursor
        ? query(
            base,
            where('isPublished', '==', true),
            orderBy('createdAt', 'desc'),
            limit(BLOG_PAGE_SIZE),
            startAfter(cursor)
          )
        : query(
            base,
            where('isPublished', '==', true),
            orderBy('createdAt', 'desc'),
            limit(BLOG_PAGE_SIZE)
          )
      const snapshot = await getDocs(q)
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const newLast = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null
      setLastDoc(newLast)
      setHasMore(snapshot.docs.length === BLOG_PAGE_SIZE)
      if (append) {
        setBlogPosts(prev => [...prev, ...posts])
        return posts
      } else {
        setBlogPosts(posts)
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error)
      setHasMore(false)
      if (append) return []
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (postId) {
      fetchPost(postId)
      // Keep list loaded for prev/next navigation; load first page if not yet loaded
      if (blogPosts.length === 0) {
        setLastDoc(null)
        setHasMore(true)
        fetchPostsPage(null, false)
      }
    } else {
      setLastDoc(null)
      setHasMore(true)
      fetchPostsPage(null, false)
    }
  }, [postId, fetchPostsPage])

  // Reading view: open from top; Prev/Next/Back to Blog also scroll destination to top
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [postId])

  useEffect(() => {
    if (postId || !hasMore || loading || loadingMore) return
    const el = loadMoreRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore && lastDoc) {
          const sentinelEl = loadMoreRef.current
          if (sentinelEl) {
            const rect = sentinelEl.getBoundingClientRect()
            scrollRestoreRef.current = {
              sentinelTopBefore: rect.top + window.scrollY,
              scrollYBefore: window.scrollY
            }
          }
          fetchPostsPage(lastDoc, true)
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [postId, hasMore, loading, loadingMore, lastDoc, fetchPostsPage])

  // After loading more, keep scroll so the loader stays at the same viewport position
  useEffect(() => {
    if (loadingMore || !scrollRestoreRef.current) return
    const el = loadMoreRef.current
    const saved = scrollRestoreRef.current
    scrollRestoreRef.current = null
    if (!el) return
    const raf = requestAnimationFrame(() => {
      const sentinelTopAfter = el.getBoundingClientRect().top + window.scrollY
      const delta = sentinelTopAfter - saved.sentinelTopBefore
      if (delta !== 0) {
        window.scrollTo(window.scrollX, saved.scrollYBefore + delta)
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [loadingMore])

  const fetchPost = async (id) => {
    try {
      setPostLoading(true)
      const postDoc = await getDoc(doc(db, 'blogPosts', id))
      if (postDoc.exists() && postDoc.data().isPublished) {
        setSelectedPost({ id: postDoc.id, ...postDoc.data() })
      } else {
        setSelectedPost(null)
      }
    } catch (error) {
      console.error('Error fetching blog post:', error)
    } finally {
      setPostLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  const getDisplayAuthor = (post) => {
    const a = post?.author?.trim?.() || ''
    if (!a || /kparasiteprise|kparasite/i.test(a)) return 'Spa~Radise Luxe team'
    return a
  }

  const getPostShareUrl = (id) => `${typeof window !== 'undefined' ? window.location.origin : ''}/blog?post=${id}`
  const shareLinks = (post) => {
    const url = getPostShareUrl(post.id)
    const title = post.title || 'Blog post'
    const text = `${title} ${url}`
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text)}`
    }
  }

  // If viewing a single post
  if (postId) {
    if (postLoading) {
      return (
        <div className="bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50 flex items-center justify-center py-20">
          <Loader className="w-12 h-12 animate-spin text-purple-500" />
        </div>
      )
    }

    if (!selectedPost) {
      return (
        <div className="bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
          <div className="container mx-auto px-4 py-16 text-center">
            <FileText className="w-24 h-24 mx-auto text-gray-400 mb-4" />
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Post Not Found</h1>
            <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Blog</span>
            </Link>
          </div>
        </div>
      )
    }

    return (
      <div className="bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
        {/* Hero Section */}
        {selectedPost.featuredImageUrl && (
          <div className="relative h-96 overflow-hidden">
            <img
              src={selectedPost.featuredImageUrl}
              alt={selectedPost.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="container mx-auto">
                <Link
                  to="/blog"
                  className="inline-flex items-center space-x-2 text-white/80 hover:text-white mb-4"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back to Blog</span>
                </Link>
                <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">{selectedPost.title}</h1>
                <div className="flex items-center space-x-4 text-sm flex-wrap gap-y-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>{getDisplayAuthor(selectedPost)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(selectedPost.publishedAt || selectedPost.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-white/80 text-xs">Share:</span>
                    <a href={shareLinks(selectedPost).facebook} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white p-1 rounded" aria-label="Share on Facebook">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </a>
                    <a href={shareLinks(selectedPost).twitter} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white p-1 rounded" aria-label="Share on X">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </a>
                    <a href={shareLinks(selectedPost).linkedin} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white p-1 rounded" aria-label="Share on LinkedIn">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                    <a href={shareLinks(selectedPost).whatsapp} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white p-1 rounded" aria-label="Share on WhatsApp">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Post Content */}
        <article className="container mx-auto px-4 py-12 pb-20 max-w-4xl">
          {!selectedPost.featuredImageUrl && (
            <div className="mb-8">
              <Link
                to="/blog"
                className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Blog</span>
              </Link>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">{selectedPost.title}</h1>
              <div className="flex items-center space-x-4 text-gray-600 mb-6 flex-wrap gap-y-2">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>{getDisplayAuthor(selectedPost)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(selectedPost.publishedAt || selectedPost.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-gray-500 text-sm">Share:</span>
                  <a href={shareLinks(selectedPost).facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#1877f2] p-1 rounded" aria-label="Share on Facebook">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a href={shareLinks(selectedPost).twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#1da1f2] p-1 rounded" aria-label="Share on X">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                  <a href={shareLinks(selectedPost).linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#0a66c2] p-1 rounded" aria-label="Share on LinkedIn">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                  <a href={shareLinks(selectedPost).whatsapp} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#25d366] p-1 rounded" aria-label="Share on WhatsApp">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                </div>
              </div>
            </div>
          )}

          {selectedPost.tags && selectedPost.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {selectedPost.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                >
                  <Tag className="w-3 h-3" />
                  <span>{tag}</span>
                </span>
              ))}
            </div>
          )}

          <div className="prose prose-lg max-w-none">
            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {selectedPost.content}
            </div>
          </div>

          {/* Prev / Back / Next */}
          <nav className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {(() => {
                const currentIndex = blogPosts.findIndex(p => p.id === selectedPost.id)
                const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null
                const nextPost = currentIndex >= 0 && currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null
                const atLastLoaded = currentIndex === blogPosts.length - 1 && blogPosts.length > 0
                return (
                  <>
                    {prevPost ? (
                      <Link
                        to={`/blog?post=${prevPost.id}`}
                        className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Previous</span>
                      </Link>
                    ) : (
                      <span className="inline-flex items-center space-x-2 px-4 py-2 text-gray-400 cursor-default" aria-hidden>
                        <ArrowLeft className="w-4 h-4" />
                        <span>Previous</span>
                      </span>
                    )}
                    <Link
                      to="/blog"
                      className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back to Blog</span>
                    </Link>
                    {nextPost ? (
                      <Link
                        to={`/blog?post=${nextPost.id}`}
                        className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium transition-colors"
                      >
                        <span>Next</span>
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    ) : atLastLoaded && hasMore && !loadingMore ? (
                      <button
                        type="button"
                        onClick={async () => {
                          setLoadingNextForNav(true)
                          const newPosts = await fetchPostsPage(lastDoc, true)
                          setLoadingNextForNav(false)
                          if (newPosts?.length) {
                            setSearchParams({ post: newPosts[0].id })
                            setSelectedPost(newPosts[0])
                          }
                        }}
                        className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-medium transition-colors disabled:opacity-70"
                      >
                        {loadingNextForNav ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            <span>Load next…</span>
                          </>
                        ) : (
                          <>
                            <span>Next</span>
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    ) : atLastLoaded && !hasMore ? (
                      <span className="inline-flex items-center space-x-2 px-4 py-2 text-gray-400 cursor-default" aria-hidden>
                        <span>Next</span>
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    ) : currentIndex < 0 ? (
                      <span className="inline-flex items-center space-x-2 px-4 py-2 text-gray-400 cursor-default" aria-hidden>
                        <span>Next</span>
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    ) : null}
                  </>
                )
              })()}
            </div>
          </nav>
        </article>
      </div>
    )
  }

  // Blog listing page
  return (
    <div className="bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
      {/* Hero Section - image2 background like Gallery */}
      <div
        className="relative text-white py-16 md:py-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/media/images/hero/image2.png)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 drop-shadow-lg">Our Blog</h1>
          <p className="text-lg md:text-xl text-white/95 max-w-2xl mx-auto drop-shadow-md">
            Wellness tips, spa insights, and beauty advice from the Spa~Radise Luxe team
          </p>
        </div>
      </div>

      {/* Blog Posts */}
      <div className="container mx-auto px-4 py-12 pb-20">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="w-12 h-12 animate-spin text-purple-500" />
          </div>
        ) : blogPosts.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-24 h-24 mx-auto text-gray-400 mb-4" />
            <p className="text-xl text-gray-600">No blog posts available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog?post=${post.id}`}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105"
              >
                {post.featuredImageUrl && (
                  <div className="aspect-video bg-gray-200 overflow-hidden">
                    <img
                      src={post.featuredImageUrl}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x225?text=Blog+Post'
                      }}
                    />
                  </div>
                )}
                <div className="p-6">
                  <h2 className="text-xl font-serif font-bold text-gray-900 mb-2 line-clamp-2">
                    {post.title}
                  </h2>
                  {post.excerpt && (
                    <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  )}
                  <div className="mt-4 mb-4">
                    <span className="inline-flex items-center space-x-2 text-purple-600 font-semibold text-sm hover:text-purple-700 transition-colors">
                      <span>Read More</span>
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>{getDisplayAuthor(post)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100" onClick={(e) => e.preventDefault()}>
                    <span className="text-xs text-gray-500 mr-1">Share:</span>
                    <a href={shareLinks(post).facebook} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-[#1877f2] transition-colors p-1 rounded" aria-label="Share on Facebook">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </a>
                    <a href={shareLinks(post).twitter} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-[#1da1f2] transition-colors p-1 rounded" aria-label="Share on X">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </a>
                    <a href={shareLinks(post).linkedin} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-[#0a66c2] transition-colors p-1 rounded" aria-label="Share on LinkedIn">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                    <a href={shareLinks(post).whatsapp} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-[#25d366] transition-colors p-1 rounded" aria-label="Share on WhatsApp">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </a>
                  </div>
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {post.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
        {/* Sentinel for infinite scroll */}
        {!loading && blogPosts.length > 0 && !postId && (
          <div ref={loadMoreRef} className="flex justify-center py-8 min-h-[60px]">
            {loadingMore && (
              <Loader className="w-8 h-8 animate-spin text-purple-500" aria-hidden />
            )}
            {!hasMore && blogPosts.length > 0 && (
              <p className="text-sm text-gray-500">You've seen all posts</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Blog
